import React from 'react';
import { Button } from '../../../components/Bootstrap';
import { States } from '../../../constants';
import { Form } from '../../../components';
import { VenueInput, TipType } from './../../../globalTypes';
import { VenueQuery_venue as Venue } from './__generated__/VenueQuery';

const PhoneImg = require('./phone.png');

const labelForTipType = (venue: Venue): string => {
  switch (venue.tipPolicy) {
    case TipType.CASH_ONLY:
      return 'Cash only tips';
    case TipType.REQUIRED_TIP:
      return `Required ${venue.requiredTipPercentage}% Tip`;
    case TipType.TIP:
      return 'Accept tips through the app';
  }
};

const schema = {
  properties: {
    name: {
      id: '/properties/name',
      type: 'string',
      title: 'Venue Name',
    },
    street1: {
      id: '/properties/street1',
      type: 'string',
      title: 'Address',
    },
    city: {
      id: '/properties/city',
      type: 'string',
      title: 'City',
    },
    state: {
      id: '/properties/state',
      type: 'string',
      title: 'State',
      enum: Object.keys(States),
      enumNames: Object.values(States),
    },
    zip: {
      id: '/properties/zip',
      type: 'string',
      title: 'Zip Code',
    },
    image: {
      id: '/properties/image',
      type: 'object',
      title: 'Upload venue photo',
    },
    maxDrinks: {
      id: '/properties/maxDrinks',
      type: ['number', 'null'],
      title: 'Max drinks per customer',
    },
    salesTaxPercentage: {
      id: '/properties/salesTaxPercentage',
      type: ['number', 'null'],
      title: 'Sales Tax Percentage',
    },
    tipPolicy: {
      type: 'string',
      title: 'Tip Policy',
      enum: ['TIP', 'CASH_ONLY', 'REQUIRED_TIP'],
      enumNames: ['Allow tipping in the app', 'Cash Only', 'Require tip percentage'],
    },
  },
  type: 'object',
  dependencies: {
    tipPolicy: {
      oneOf: [
        {
          properties: {
            tipPolicy: {
              enum: ['REQUIRED_TIP'],
            },
            requiredTipPercentage: {
              type: 'number',
              title: 'Required Tip Percentage',
            },
          },
          required: ['requiredTipPercentage'],
        },
      ],
    },
  },
};

const uiSchema = {
  image: {
    'ui:field': 'imageUploadField',
    'ui:options': {
      sizeRestrictions: {
        max: 600,
        copy: 'Images must be less than 600 KB. Recommended size: 750px x 280px',
      },
      aspectRatio: 750 / 280,
      errorText: 'Venue image required',
    },
  },
  'ui:order': ['*', 'image'],
};

interface InfoSectionProps {
  venue: Venue;
  editing: boolean;
  onSubmit(data: VenueInput);
  handleCancel();
}

export class InfoSection extends React.Component<InfoSectionProps> {
  renderForm() {
    const { venue } = this.props;

    const formProps = {
      className: 'manage-venue-form',
      uiSchema,
      onSubmit: this.onSubmit,
      formData: {
        name: venue.name,
        imageUrl: venue.imageUrl,
        street1: venue.address.street1,
        city: venue.address.city,
        state: venue.address.state,
        zip: venue.address.zip,
        maxDrinks: venue.config && venue.config.drinkUnitLimitPerPerson ? venue.config.drinkUnitLimitPerPerson : null,
        salesTaxPercentage: venue.salesTaxPercentage,
        tipPolicy: venue.tipPolicy,
        requiredTipPercentage: venue.requiredTipPercentage,
      },
      schema,
    };

    return (
      <Form {...formProps}>
        <div>
          <Button className='d-none d-md-inline-block' onClick={this.props.handleCancel} color='link'>
            Cancel
          </Button>
          <Button type='submit' color='primary' className='btn--rounded-padded'>
            Submit
          </Button>
        </div>
      </Form>
    );
  }

  renderNonForm() {
    const { name, address, imageUrl, config, salesTaxPercentage } = this.props.venue;
    const maxDrinks = config && config.drinkUnitLimitPerPerson ? config.drinkUnitLimitPerPerson : null;

    return (
      <div>
        <p className='manage-venue-copy'>{name}</p>
        <p className='manage-venue-copy'>{address.street1}</p>
        <p className='manage-venue-copy'>
          {address.city}, {address.state} {address.zip}
        </p>
        <p className='manage-venue-copy'>
          {maxDrinks ? `Max ${maxDrinks} drinks per customer` : 'No max drinks per customer'}
        </p>
        <p className='manage-venue-copy'>{salesTaxPercentage ? `${salesTaxPercentage}% Sales Tax` : 'No sales tax'}</p>
        <p className='manage-venue-copy'>{labelForTipType(this.props.venue)}</p>
        <div className='phone-wrapper'>
          <img className='img-fluid' src={PhoneImg} />
          <div className='phone-content'>
            <div className='phone-venue-preview'>
              <img src={imageUrl} />
            </div>
            <div className='phone-dummy-row'>&nbsp;</div>
          </div>
        </div>
      </div>
    );
  }

  onSubmit = ({ formData: data }) => {
    const submitData: any = {
      name: data.name,
      address: {
        id: this.props.venue.address.id,
        name: data.name,
        street1: data.street1,
        city: data.city,
        state: data.state,
        zip: data.zip,
      },
      salesTaxPercentage: data.salesTaxPercentage,
      tipPolicy: data.tipPolicy,
      requiredTipPercentage: data.requiredTipPercentage,
    };

    if (data.image) {
      submitData.image = data.image;
    }

    if (data.maxDrinks && !isNaN(Number(data.maxDrinks))) {
      submitData.config = {
        ...this.props.venue.config,
        drinkUnitLimitPerPerson: Number(data.maxDrinks),
      };
    }

    this.props.onSubmit(submitData);
  };

  render() {
    if (this.props.editing) {
      return this.renderForm();
    } else {
      return this.renderNonForm();
    }
  }
}
